import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, visible };
}

export function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-[1400ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] ${
        visible ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-[6px]"
      } ${className}`}
    >
      {children}
    </div>
  );
}

export function Wordmark({ className = "" }: { className?: string }) {
  return <span className={className}>MISTVALE</span>;
}

const menuLinks = [
  { label: "Ranges", href: "/#ranges" },
  { label: "Estate", href: "/#about" },
  { label: "About Us", href: "/#about" },
  { label: "Journal", href: "/#journal" },
  { label: "Trace Your Tea", href: "/#trace" },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);
  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-700 ${
        scrolled && !open
          ? "bg-ivory/85 backdrop-blur-md text-forest border-b border-forest/10"
          : open
            ? "bg-transparent text-ivory"
            : "bg-transparent text-ivory"
      }`}
    >
      {/* announcement bar — collapses once the page is scrolled */}
      <div
        className={`overflow-hidden bg-forest text-center text-[10px] uppercase tracking-[0.24em] text-gold transition-all duration-500 ${
          scrolled && !open ? "max-h-0" : "max-h-10 py-2"
        }`}
      >
        Complimentary worldwide shipping on sample tins · Est. 1927
      </div>
      <div className="relative z-10 mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-5 md:px-10">
        <nav className="hidden gap-8 text-[11px] uppercase tracking-[0.22em] md:flex">
          <a href="/#ranges" className="hover:opacity-70 transition-opacity">Ranges</a>
          <a href="/#about" className="hover:opacity-70 transition-opacity">Estate</a>
        </nav>
        <a href="/#top" className="group flex flex-col items-center justify-self-center">
          <Wordmark
            className={`${
              scrolled && !open ? "brand-wordmark" : "brand-wordmark-light"
            } text-2xl leading-none transition-transform duration-500 group-hover:scale-[1.02] md:text-3xl`}
          />
          <span className="brand-tagline mt-1.5">Primum Ceylon Tea</span>
        </a>
        <nav className="hidden justify-end gap-8 text-[11px] uppercase tracking-[0.22em] md:flex">
          <a href="/#journal" className="hover:opacity-70 transition-opacity">Journal</a>
          <a href="/#about" className="hover:opacity-70 transition-opacity">About Us</a>
          <a href="/#enquiry" className="hover:opacity-70 transition-opacity">Contact</a>
        </nav>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          className="md:hidden col-start-3 justify-self-end text-[11px] uppercase tracking-[0.22em]"
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      {/* mobile menu overlay */}
      <div
        className={`fixed inset-0 bg-forest text-ivory transition-opacity duration-500 md:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <nav className="flex h-full flex-col justify-center gap-2 px-10">
          {menuLinks.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              style={{ transitionDelay: open ? `${120 + i * 60}ms` : "0ms" }}
              className={`font-serif text-4xl transition-all duration-500 hover:text-gold ${
                open ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
            >
              {l.label}
            </a>
          ))}
          <div className="mt-10 text-[11px] uppercase tracking-[0.22em] text-ivory/50">
            <span>
              Nuwara Eliya · Sri Lanka
              <br />
              hello@mistvale.tea
            </span>
          </div>
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-ivory/10 bg-forest text-ivory/80">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-14">
        <div className="grid gap-12 md:grid-cols-5">
          <div>
            <div className="flex flex-col items-start gap-1.5">
              <Wordmark className="brand-wordmark-light text-2xl" />
              <span className="brand-tagline">Primum Ceylon Tea</span>
            </div>
            <p className="mt-4 text-sm font-light leading-relaxed text-ivory/60">
              Ultra-premium Ceylon tea, exported by hand from Nuwara Eliya, Sri Lanka.
            </p>
          </div>
          <div>
            <div className="eyebrow mb-4 text-gold">Shop</div>
            <ul className="space-y-2 text-sm font-light">
              <li><Link to="/tea/ceylon-sovereign" className="transition hover:text-gold">Black Tea</Link></li>
              <li><Link to="/tea/silver-whisper" className="transition hover:text-gold">White Tea</Link></li>
              <li><Link to="/tea/rose-cardamom-noir" className="transition hover:text-gold">Blended Tea</Link></li>
              <li><a href="/#ranges" className="transition hover:text-gold">Estate Reserve</a></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4 text-gold">House</div>
            <ul className="space-y-2 text-sm font-light">
              <li><a href="/#top" className="transition hover:text-gold">Our Story</a></li>
              <li><a href="/#about" className="transition hover:text-gold">About Us</a></li>
              <li><a href="/#trace" className="transition hover:text-gold">Trace Your Tea</a></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4 text-gold">Help</div>
            <ul className="space-y-2 text-sm font-light">
              <li><a href="#" className="transition hover:text-gold">Delivery &amp; Export</a></li>
              <li><a href="#" className="transition hover:text-gold">Returns</a></li>
              <li><a href="#" className="transition hover:text-gold">FAQs</a></li>
              <li><a href="#" className="transition hover:text-gold">Terms &amp; Conditions</a></li>
              <li><a href="#" className="transition hover:text-gold">Privacy Policy</a></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4 text-gold">Contact Us</div>
            <p className="text-sm font-light leading-relaxed">
              Diligence Enterprises (Pvt) Ltd<br />
              Reg. No. PV00247092
            </p>
            <p className="mt-3 text-sm font-light leading-relaxed">
              No 47, Malwattagama,<br />
              Kurunduwatta Bazaar,<br />
              Gampola, Sri Lanka
            </p>
            <p className="mt-3 text-sm font-light leading-relaxed">
              diligencegroup@gmail.com
            </p>
            <a
              href="/#enquiry"
              className="mt-4 inline-block border-b border-gold/50 pb-1 text-[11px] uppercase tracking-[0.22em] text-gold transition hover:text-ivory"
            >
              Trade Enquiries →
            </a>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-ivory/10 pt-8 text-[11px] uppercase tracking-[0.22em] text-ivory/50 md:flex-row md:items-center">
          <span>© {new Date().getFullYear()} Mistvale Tea. All rights reserved.</span>
          <span>Handcrafted in Ceylon · Shipped worldwide</span>
        </div>
      </div>
    </footer>
  );
}
